import React, { useState } from 'react';
import FormInput from '../common/FormInput';
import FormButton from '../common/FormButton';
import GoogleSignInButton from '../common/GoogleSignInButton';
import { useAuth } from '../../../contexts/AuthContext';

const StepOne = ({ formData, onNext, onBackToHome, onNavigate }) => {
  const { googleLogin, previousPage } = useAuth();
  const [stepData, setStepData] = useState({
    email: formData.email || '',
    password: formData.password || '',
    confirmPassword: formData.confirmPassword || ''
  });
  const [errors, setErrors] = useState({});

  const handleChange = (e) => {
    const { name, value } = e.target;
    setStepData(prev => ({
      ...prev,
      [name]: value
    }));
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateStep = () => {
    const newErrors = {};

    if (!stepData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(stepData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    if (!stepData.password) {
      newErrors.password = 'Password is required';
    } else if (stepData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters';
    }

    if (!stepData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (stepData.password !== stepData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateStep()) {
      onNext(stepData);
    }
  };

  return (
    <div className="space-y-6">
      <div className="mb-4">
        <button
          onClick={onBackToHome}
          className="text-blue-600 hover:text-blue-800 text-sm font-medium transition-colors"
        >
          ← Back to Home
        </button>
      </div>

      <div className="text-center mb-6">
        <h3 className="text-xl font-semibold text-gray-900 mb-2">Step 1: Account Details</h3>
        <p className="text-gray-600">Sign up with Google or use your email</p>
      </div>

      {errors.general && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-md text-sm">
          {errors.general}
        </div>
      )}

      <GoogleSignInButton
        label="Sign up with Google"
        onSuccess={async (accessToken) => {
          await googleLogin(accessToken);
          onNavigate?.(previousPage || 'main');
        }}
        onError={(message) => setErrors({ general: message })}
      />

      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <div className="w-full border-t border-gray-300" />
        </div>
        <div className="relative flex justify-center text-sm">
          <span className="px-2 bg-white text-gray-500">Or sign up with email</span>
        </div>
      </div>

      <FormInput
        label="Email Address"
        type="email"
        name="email"
        value={stepData.email}
        onChange={handleChange}
        placeholder="Enter your email address"
        required
        error={errors.email}
      />

      <FormInput
        label="Password"
        type="password"
        name="password"
        value={stepData.password}
        onChange={handleChange}
        placeholder="Create a strong password"
        required
        error={errors.password}
        helpText="Must be at least 8 characters long"
      />

      <FormInput
        label="Confirm Password"
        type="password"
        name="confirmPassword"
        value={stepData.confirmPassword}
        onChange={handleChange}
        placeholder="Confirm your password"
        required
        error={errors.confirmPassword}
      />

      <FormButton
        onClick={handleNext}
        disabled={!stepData.email || !stepData.password || !stepData.confirmPassword}
        className="w-full"
      >
        Continue to Personal Information
      </FormButton>

      <div className="text-center">
        <p className="text-gray-600">
          Already have an account?{' '}
          <button
            type="button"
            onClick={() => onNext({ navigateTo: 'login' })}
            className="text-blue-600 hover:text-blue-800 font-medium transition-colors"
          >
            Sign in
          </button>
        </p>
      </div>
    </div>
  );
};

export default StepOne;
